/**
 * NlQueryReportInterfaceAiNativePage — single form for `nl-query-report-interface-ai-native`. POSTs via useNlQueryReportInterfaceAiNativeCreate.
 */
import { useState, type FormEvent } from "react";
import { useNlQueryReportInterfaceAiNativeCreate } from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";

export function NlQueryReportInterfaceAiNativePage() {
	const createMut = useNlQueryReportInterfaceAiNativeCreate();
	const [name, setName] = useState("");
	const [notes, setNotes] = useState("");
	const [saved, setSaved] = useState(false);

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setSaved(false);
		createMut.mutate(
			{ name: name.trim(), notes: notes.trim() },
			{
				onSuccess: () => {
					setSaved(true);
					setName("");
					setNotes("");
				},
			},
		);
	};

	return (
		<main
			data-testid="nl-query-report-interface-ai-native-page"
			className="mx-auto flex w-full max-w-2xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<h1 className="text-green-700 text-3xl font-semibold">
					NlQueryReportInterfaceAiNative
				</h1>
			</header>

			<form
				onSubmit={handleSubmit}
				className={`${CARD} flex flex-col gap-4`}
				data-testid="nl-query-report-interface-ai-native-form"
				aria-label="nl-query-report-interface-ai-native form"
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="nl-query-report-interface-ai-native-name" className={LABEL}>
						Name
					</label>
					<input
						id="nl-query-report-interface-ai-native-name"
						type="text"
						required
						className={INPUT}
						value={name}
						onChange={(e) => setName(e.currentTarget.value)}
						data-testid="nl-query-report-interface-ai-native-name-input"
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="nl-query-report-interface-ai-native-notes" className={LABEL}>
						Notes
					</label>
					<textarea
						id="nl-query-report-interface-ai-native-notes"
						rows={4}
						className={INPUT}
						value={notes}
						onChange={(e) => setNotes(e.currentTarget.value)}
						data-testid="nl-query-report-interface-ai-native-notes-input"
					/>
				</div>

				<div className="flex items-center gap-3">
					<button
						type="submit"
						className={BTN}
						disabled={createMut.isPending}
						data-testid="nl-query-report-interface-ai-native-save"
					>
						{createMut.isPending ? "Saving." : "Save"}
					</button>
					{saved ? (
						<p className="text-sm text-green-700" role="status">
							Saved.
						</p>
					) : null}
					{createMut.error ? (
						<p className="text-sm text-red-600" role="alert">
							{createMut.error.message}
						</p>
					) : null}
				</div>
			</form>
		</main>
	);
}

export default NlQueryReportInterfaceAiNativePage;
